<template>
  <div class="lesound-page">
    <section class="hero">
      <div class="hero-copy">
        <span class="eyebrow">AI WEARABLE COMPANION APP</span>
        <div class="brand-lockup">
          <img :src="logoImage" :alt="copy.logoAlt" class="product-logo">
          <h1>{{ copy.productName }}</h1>
        </div>
        <h2>{{ copy.subtitle }}</h2>
        <p>{{ copy.description }}</p>
        <div class="device-tags">
          <span>{{ copy.devices }}</span>
          <span>{{ copy.effortless }}</span>
        </div>
      </div>
      <div class="hero-visual">
        <img :src="heroImage" :alt="copy.heroAlt">
      </div>
    </section>

    <section class="features-section">
      <div class="section-heading">
        <span class="section-kicker">{{ copy.featureKicker }}</span>
        <h2>{{ copy.featureTitle }}</h2>
        <p>{{ copy.featureIntro }}</p>
      </div>
      <div class="feature-grid">
        <article v-for="feature in features" :key="feature.title" class="feature-card">
          <div class="feature-image">
            <img :src="feature.image" :alt="feature.title">
          </div>
          <div class="feature-copy">
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </article>
      </div>
    </section>

    <app-download-panel
      :is-english="isEnglish"
      :product-name="copy.productName"
      ios-url="https://www.pgyer.com/lesound-ios"
      android-url="https://www.pgyer.com/lesound-android"
      accent="#2588d7"
      glow="rgba(76, 168, 241, .18)"
    />

  </div>
</template>

<script>
import AppDownloadPanel from '@/components/AppDownloadPanel';

const zhCopy = {
  productName: '乐音',
  logoAlt: '乐音 Logo',
  subtitle: '让视觉与声音，协同理解每个场景',
  description: '乐音是面向 AI 眼镜与 AI 耳机的一站式配套应用，统一连接设备、调校核心能力并生成场景化报告，让智能穿戴开箱即用、无感协同。',
  devices: 'AI 眼镜与 AI 耳机',
  effortless: '无感连接',
  heroAlt: '乐音 AI 眼镜、AI 耳机与手机设备管理应用',
  featureKicker: '三大核心能力',
  featureTitle: '两类设备，一个智能入口',
  featureIntro: '降低设备使用门槛，释放视觉与音频能力，并用场景数据持续优化体验。',
  features: [
    ['一站式设备管理', '集中完成 AI 眼镜与 AI 耳机的配网、固件升级和功能设置，实现开箱即用、无感连接。'],
    ['跨设备能力调校', '统一管理眼镜的视觉交互、语音指令以及耳机的降噪、音频增强，适配通勤、运动和办公场景。'],
    ['场景化使用报告', '同步视觉辅助数据与音频互动记录，分析运动时的指令效率、办公时的降噪效果等真实体验。']
  ]
};

const enCopy = {
  productName: 'Le-Sound',
  logoAlt: 'Le-Sound logo',
  subtitle: 'Bring vision and sound together for every moment',
  description: 'Le-Sound is the all-in-one companion app for AI glasses and AI earbuds, unifying device connection, capability tuning, and scenario-based insights in one seamless experience.',
  devices: 'AI Glasses & AI Earbuds',
  effortless: 'Effortless Connection',
  heroAlt: 'Le-Sound AI glasses, AI earbuds, and mobile device management app',
  featureKicker: 'THREE CORE CAPABILITIES',
  featureTitle: 'Two device types, one intelligent hub',
  featureIntro: 'Simplify setup, unlock visual and audio capabilities, and continuously improve experiences with scenario data.',
  features: [
    ['All-in-One Device Management', 'Set up connectivity, update firmware, and configure features for AI glasses and earbuds from one effortless control center.'],
    ['Cross-Device Capability Tuning', 'Tune visual interaction and voice commands for glasses, plus noise cancellation and audio enhancement for commuting, sports, and work.'],
    ['Scenario-Based Usage Reports', 'Combine visual-assistance data and audio interactions to analyze command efficiency, noise-control performance, and real-world usage.']
  ]
};

const featureImages = [
  require('@/assets/images/lesound/device-management.webp'),
  require('@/assets/images/lesound/personalization.webp'),
  require('@/assets/images/lesound/scenario-reports.webp')
];

export default {
  name: 'LeSound',
  components: { AppDownloadPanel },
  data() {
    return {
      heroImage: require('@/assets/images/lesound/hero.webp'),
      logoImage: require('@/assets/images/lesound/logo.webp')
    };
  },
  computed: {
    isEnglish() {
      return this.$i18n.locale.indexOf('en') !== -1;
    },
    copy() {
      return this.isEnglish ? enCopy : zhCopy;
    },
    features() {
      return this.copy.features.map((feature, index) => ({
        title: feature[0],
        description: feature[1],
        image: featureImages[index]
      }));
    }
  },
  mounted() {
    document.body.className = 'page-template-default page page-one-column colors-light';
  }
};
</script>

<style lang="stylus" scoped>
.lesound-page
  color #14213d
  background #f7f9ff
  overflow hidden

.hero
  min-height 650px
  display grid
  grid-template-columns minmax(0, 0.85fr) minmax(520px, 1.3fr)
  align-items center
  max-width 1440px
  margin 0 auto
  padding 72px 48px 80px
  gap 56px
  position relative

  &::before
    content ''
    position absolute
    width 480px
    height 480px
    border-radius 50%
    background rgba(76, 168, 241, 0.14)
    filter blur(80px)
    left -260px
    top 30px

.hero-copy
  position relative
  z-index 1

  .eyebrow
    display inline-block
    color #2588d7
    font-size 12px
    font-weight 700
    letter-spacing 0.18em
    margin-bottom 18px

  h2
    margin 28px 0 18px
    font-size clamp(25px, 2.5vw, 40px)
    line-height 1.25
    letter-spacing -0.03em

  p
    max-width 570px
    margin 0
    color #5c6784
    font-size 17px
    line-height 1.85

.brand-lockup
  display flex
  align-items center
  gap 22px

  h1
    margin 0
    font-size clamp(64px, 7vw, 104px)
    line-height 0.95
    letter-spacing -0.06em
    background linear-gradient(120deg, #174d9e 0%, #48a7f4 52%, #9ee7ef 100%)
    -webkit-background-clip text
    -webkit-text-fill-color transparent

.product-logo
  width clamp(94px, 9vw, 136px)
  height clamp(72px, 7vw, 104px)
  object-fit contain
  background #0b0f19
  border-radius 22px
  box-shadow 0 16px 36px rgba(37, 136, 215, 0.2)

.device-tags
  display flex
  flex-wrap wrap
  gap 10px
  margin-top 30px

  span
    padding 9px 16px
    color #42517b
    background rgba(255, 255, 255, 0.82)
    border 1px solid rgba(75, 99, 190, 0.14)
    border-radius 999px
    font-size 13px
    box-shadow 0 8px 24px rgba(55, 72, 130, 0.08)

.hero-visual
  position relative
  border-radius 32px
  overflow hidden
  box-shadow 0 36px 80px rgba(28, 38, 101, 0.25)
  transform rotate(1deg)

  &::after
    content ''
    position absolute
    inset 0
    border 1px solid rgba(255, 255, 255, 0.24)
    border-radius inherit
    pointer-events none

  img
    display block
    width 100%
    aspect-ratio 1.7
    object-fit cover

.features-section
  padding 100px 48px 120px
  background #fff

.section-heading
  max-width 720px
  margin 0 auto 56px
  text-align center

  .section-kicker
    color #2588d7
    font-size 12px
    font-weight 700
    letter-spacing 0.16em

  h2
    margin 14px 0 16px
    color #15244a
    font-size clamp(32px, 4vw, 50px)
    line-height 1.2
    letter-spacing -0.04em

  p
    margin 0
    color #71809d
    font-size 17px
    line-height 1.75

.feature-grid
  display grid
  grid-template-columns repeat(3, minmax(0, 1fr))
  max-width 1180px
  margin 0 auto
  gap 24px

.feature-card
  min-width 0
  overflow hidden
  background #f7f9ff
  border 1px solid #e8edfa
  border-radius 24px
  transition transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease

  &:hover
    transform translateY(-7px)
    border-color #d7dffa
    box-shadow 0 24px 56px rgba(36, 55, 112, 0.13)

.feature-image
  height 220px
  overflow hidden
  background #f3f6fb
  padding 10px
  box-sizing border-box

  img
    width 100%
    height 100%
    object-fit contain
    object-position center

.feature-copy
  padding 26px 28px 30px

  h3
    margin 0 0 11px
    color #192b55
    font-size 21px

  p
    margin 0
    color #6b7894
    font-size 15px
    line-height 1.7

@media (max-width: 980px)
  .hero
    grid-template-columns 1fr
    gap 44px
    padding 64px 32px 72px

  .hero-copy
    text-align center

    p
      margin 0 auto

  .device-tags
    justify-content center

  .hero-visual
    max-width 760px
    margin 0 auto

  .feature-grid
    grid-template-columns repeat(2, minmax(0, 1fr))

@media (max-width: 640px)
  .hero
    min-height auto
    padding 52px 18px 56px

  .hero-copy
    h2
      margin-top 22px
      font-size 27px

    p
      font-size 15px

  .brand-lockup
    justify-content center
    gap 14px

    h1
      font-size 58px

  .product-logo
    width 82px
    height 62px
    border-radius 16px

  .hero-visual
    border-radius 22px
    transform none

  .features-section
    padding 72px 18px 84px

  .section-heading
    margin-bottom 38px

  .feature-grid
    grid-template-columns 1fr
    gap 18px

  .feature-card
    display grid
    grid-template-columns 132px 1fr
    align-items stretch
    border-radius 19px

  .feature-image
    height auto
    min-height 156px
    padding 8px

  .feature-copy
    padding 22px 18px

    h3
      font-size 18px

    p
      font-size 14px

@media (max-width: 420px)
  .feature-card
    grid-template-columns 108px 1fr

  .feature-copy
    padding 18px 14px
</style>
